import React, {
  useState, Suspense, useContext
} from 'react';
import { string, number, bool } from 'prop-types';
import classNames from 'classnames';
import { Row, Col, Rating, Alert } from '@thd-olt-component-react/core-ui';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { SalientPoints } from '@thd-olt-component-react/salient-points';
import { Price } from '@thd-olt-component-react/price';
import { QueryContext, QueryProvider } from '@thd-nucleus/data-sources';
import { Quantity } from '@thd-olt-component-react/quantity';
import { SwapItem } from '@thd-olt-component-react/swap-item';
import { FulfillmentData } from '../subcomponents/fulfillmentData/FulfillmentData';
import { useOrderAPackageContext } from '../../OrderAPackageContext';
import { ProductImage } from '../subcomponents/productImage/ProductImage';
import { ProductDescription } from '../subcomponents/productDescription/ProductDescription';
import { ProductKpf } from '../subcomponents/productKpf/ProductKpf';
import { AlternateProducts } from '../subcomponents/alternateProducts/AlternateProducts';
import { PackagePodMessage } from '../subcomponents/packagePodMessage/PackagePodMessage';
import { FulfillmentError } from '../subcomponents/fulfillmentError/FulfillmentError';
import { fulfillmentBackOrderedStatus,
  mergeProductFulfillment
  , getQuantityError } from '../fulfillmentUtils';

import {
  OUT_OF_STOCK, BACKORDERED, LIMITED_STOCK
} from '../constants';
import { AddOrRemoveButton } from '../subcomponents/addOrRemoveButton/AddOrRemoveButton';
import { ProductDetailsButton } from '../subcomponents/productDetailsButton/ProductDetailsButton';
import { FulfillmentAndActionsLayout }
from '../packagePodFulfillmentAndActionsLayout/PackagePodFulfillmentAndActionsLayout';
import { createDefaultVariables, getUniquePromoIds } from '../utils';
import './packagePod.scss';
import { useBold } from '../../hooks/useBold/useBold';
import { QuickViewLazy } from '../quickViewLazy/QuickViewLazy';
import { useTheme } from '@thd-olt-component-react/theme-provider';

const getFulfillmentErrorType = (product, backOrderedStatus) => {
  if (product.isOutOfStock) return OUT_OF_STOCK;
  if (product.isLimitedQuantity) return LIMITED_STOCK;
  if (backOrderedStatus.backOrdered) return BACKORDERED;
  return null;
};

export const PackagePod = ({
  itemId, storeId, index
}) => {

  const theme = useTheme(PackagePod);
  const { showAddToList } = theme.props;

  const {
    state, channel, actions, dispatch, fulfillmentLoaded
  } = useOrderAPackageContext();
  const { defaultVariables } = useContext(QueryContext) || {};

  const [shouldShowPodMessage, setShouldShowPodMessage] = useState(true);
  const [fulfillmentLoader, setFulfillmentLoader] = useState(null);
  const [isRatingsOpen, setIsRatingsOpen] = useState(false);
  const { products, originalItemIds, metadata, isMajorAppliancePackage } = state;

  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [isQuickViewMounted, setIsQuickViewMounted] = useState(false);
  const { boldRef } = useBold(/\d+ in stock|\d+(?= on this product)/g);

  const product = products?.get(itemId);
  if (!product?.selected) return null;
  const {
    keyProductFeatures, productGroupSelected, media, reviews, alternateProducts, identifiers, highlights
  } = product;

  let { images } = media || {
    images: [
      {
        url: metadata?.defaultImage?.url,
        subType: 'PRIMARY'
      }
    ]
  };

  const { averageRating, totalReviews } = reviews?.ratingsReviews || {};
  const { canonicalUrl, productLabel, brandName, modelNumber } = identifiers || {};
  const newDefaultVariables = createDefaultVariables(storeId, defaultVariables);

  const isOriginalItem = originalItemIds.includes(itemId);
  const queryDataSource = isOriginalItem ? 'packages' : 'packageSwapRecs';
  const hasAlternateProducts = !!alternateProducts?.products?.length;
  const hasHighlights = !!highlights?.length;
  const hasKeyProductFeatures = keyProductFeatures?.keyProductFeaturesItems?.[0]?.features?.length >= 2;
  const showSalientPoints = !hasKeyProductFeatures && !isMajorAppliancePackage && hasHighlights;
  const previouslySelectedItemId = state?.packagePodMessages?.[itemId]?.previousItemId;
  const previouslySelectedProduct = state.allProducts[previouslySelectedItemId] || null;
  const backOrderedStatus = fulfillmentBackOrderedStatus(product);
  const [promoId = null] = getUniquePromoIds(product?.pricing?.promotionalAdjustments);

  const handleSwap = (newItem, previousItemId) => {
    dispatch(actions.swapProduct(newItem, previousItemId, index));
  };

  const handleSwapBack = () => {
    handleSwap({ ...previouslySelectedProduct, quantity: 1 }, itemId);
  };

  const swapItem = (swappedItem) => {

    if (state.isMajorAppliancePackage) {
      handleSwap(swappedItem.product, itemId);
    } else {

      setFulfillmentLoader(
        <QueryProvider
          dataSource="catalog"
          queryOptions={({ options }) => {
            return {
              ...options,
              fetchPolicy: 'no-cache'
            };
          }}
          cacheKey="package-pod-fulfillment"
          defaultVariables={newDefaultVariables}
        >
          <FulfillmentData
            onData={(data) => {
              handleSwap(mergeProductFulfillment(swappedItem.product, [data.product]), itemId);
              setFulfillmentLoader(null);
            }}
            storeId={storeId}
            itemId={swappedItem.product.itemId}
          />
        </QueryProvider>);
    }

  };
  const lowOpacityClassName = classNames({
    'package-pod--opacity-25':
      (!productGroupSelected) ||
      (product.isOutOfStock)
  });

  const openQuickView = () => {
    if (!isQuickViewOpen) {
      setIsQuickViewMounted(true);
      setIsQuickViewOpen(true);
    }
  };
  const mountQuickView = () => {
    if (!isQuickViewMounted) {
      setIsQuickViewMounted(true);
    }
  };

  let quantityError = getQuantityError(fulfillmentLoaded, product);

  return (
    <Col nopadding data-testid="package-pod" className="card package-pod__wrapper">
      <div className={lowOpacityClassName}>
        {previouslySelectedProduct && shouldShowPodMessage && (
          <Col nopadding>
            <PackagePodMessage
              product={previouslySelectedProduct}
              onClick={handleSwapBack}
              onCloseClick={() => setShouldShowPodMessage(false)}
            />
          </Col>
        )}
        <Col id={`package-pod--${itemId}`} className="package-pod" nopadding>
          <div className="package-pod__save-to-favorites">
            <QueryProvider
              cacheKey={`package-pod-favorite-${itemId}`}
              dataSource={queryDataSource}
              defaultVariables={newDefaultVariables}
            >
              {showAddToList &&
                <AddToList
                  itemId={itemId}
                  storeId={storeId}
                  showIconButton
                />
              }
            </QueryProvider>
          </div>
          <Col sm="4" className="package-pod__image">
            <ProductImage
              canonicalUrl={canonicalUrl}
              productLabel={productLabel}
              brandName={brandName}
              images={images}
            />
          </Col>
          <Col sm="8" className="package-pod__details-and-actions">
            <QueryProvider
              cacheKey={`package-pod-price-${itemId}`}
              dataSource={queryDataSource}
              defaultVariables={newDefaultVariables}
            >
              <Col className="package-pod__details-container" nopadding>
                <Col md="7" className="package-pod__ratings-model-description-container">
                  <Col nopadding>
                    <Row>
                      <button
                        type="button"
                        className="package-pod__rating-model-container"
                        onClick={() => {
                          setIsRatingsOpen(true);
                          openQuickView();
                        }}

                      >
                        <Rating value={parseFloat(averageRating || 0)} />
                        <span>({totalReviews || 0})</span>
                        {modelNumber &&
                          <span className="package-pod__model-number">Model#&nbsp;{modelNumber}</span>}
                      </button>

                    </Row>
                  </Col>
                  <Row>
                    <ProductDescription product={product} />
                  </Row>
                </Col>
                <Col md="1" className="package-pod__blank">&nbsp;</Col>
                <Col md="4" className="package-pod__price">
                  <Price
                    itemId={itemId}
                    channel={channel}
                    storeId={storeId}
                    displayEachUom={false}
                    large={false}
                    product={product}
                  />
                </Col>
              </Col>
              {hasAlternateProducts && (
                <Col className="package-pod__alternate-products-container">
                  <AlternateProducts itemId={itemId} />
                </Col>
              )}
              {hasKeyProductFeatures && (
                <Col className="package-pod__key-product-features" data-testid="key-product-features">
                  <ProductKpf itemId={itemId} storeId={storeId} />
                </Col>
              )}
              {showSalientPoints && (
                <Col className="package-pod__salient-points">
                  <QueryProvider
                    cacheKey="selected-item-details-salient-points"
                    defaultVariables={newDefaultVariables}
                  >
                    <Col className="package-pod__salient-points__border">
                      <SalientPoints itemId={itemId} max={3} />
                    </Col>
                  </QueryProvider>
                </Col>
              )}
            </QueryProvider>
          </Col>
        </Col>
      </div>
      <Col className="package-pod__fulfillment-and-actions">
        <FulfillmentAndActionsLayout
          FulfillmentMessage={!state.isMajorAppliancePackage && (
            <FulfillmentError
              errorType={getFulfillmentErrorType(product, backOrderedStatus)}
              backOrderedStatus={backOrderedStatus}
            />
          )}
          Remove={(
            <AddOrRemoveButton
              itemId={itemId}
              productGroupSelected={product.productGroupSelected}
            />
          )}
          SwapItem={(
            <QueryProvider cacheKey="package-pod-swap-item" defaultVariables={newDefaultVariables}>
              <SwapItem
                className="package-pod__swap"
                itemId={itemId}
                onSwap={(swappedItem) => {
                  swapItem(swappedItem, state.isMajorAppliancePackage);
                }}
                promoId={promoId}
              />
            </QueryProvider>
          )}
          ProductDetailsButton={(
            <ProductDetailsButton
              onMouseEnter={mountQuickView}
              onClick={openQuickView}
            />
          )}
          Quantity={!state.isMajorAppliancePackage && !product.isOutOfStock && (
            <Quantity
              noRightMargin
              value={product.selectedQty}
              onChange={({ quantity }) => {
                dispatch(actions.setProductSelectedQuantity(itemId, quantity));
              }}
            />
          )}
          QuantityError={
            quantityError && (
              <div ref={boldRef}>
                <Alert
                  inline
                  type="warning"
                  message={quantityError}
                />
              </div>
            )
          }
        />{fulfillmentLoader}
      </Col>
      {typeof window !== 'undefined' && isQuickViewMounted && (
        <Suspense fallback={<div />}>
          <QuickViewLazy
            isOpen={isQuickViewOpen}
            onClose={() => {
              setIsQuickViewOpen(false);
              setIsRatingsOpen(false);
            }}
            fetch={isQuickViewMounted}
            itemId={itemId}
            isRatingsOpen={isRatingsOpen}
            paginateCarousel={false}
          />
        </Suspense>
      )}
    </Col>
  );
};

PackagePod.propTypes = {
  itemId: string.isRequired,
  storeId: string.isRequired,
  index: number.isRequired
};

PackagePod.defaultProps = {};

PackagePod.themeProps = {
  showAddToList: bool
};

PackagePod.defaultThemeProps = {
  showAddToList: true
};

PackagePod.displayName = 'PackagePod';
